"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.useConceptOfferHintPrices = void 0;
var _basketUpdated = require("@ori-events/basket-updated");
var _react = require("react");
var _logger = require("../logger");
const sumTotals = (items)=>items.reduce((accumulator, current)=>{
        accumulator.purchasePrice += current.purchasePrice;
        accumulator.price += current.price;
        return accumulator;
    }, {
        price: 0,
        purchasePrice: 0
    });
const getTotals = (items)=>sumTotals(items.filter((param)=>{
        let { selectedQuantity } = param;
        return selectedQuantity > 0;
    }).map((param)=>{
        let { price, purchasePrice, selectedQuantity } = param;
        return {
            price: price * selectedQuantity,
            purchasePrice: purchasePrice * selectedQuantity
        };
    }));
const useConceptOfferHintPrices = (param)=>{
    let { benefits, benefitsQuantity, items1stSection, items2ndSection, isRequirementMet, offerId, offerType, requiredQuantity, selectedBenefits } = param;
    /**
   * CHOOSE_WITH_DISCOUNT offer type has 2 situations:
   *
   * 1) requiredQuantity === benefitsQuantity - In this case, discounts can be immediatelly
   * applied to all selected items, as all will be discounted. Discounts are also
   * immediatelly visualized.
   *
   * 2) requiredQuantity > benefitsQuantity - In this case, discounts are applied to
   * benefitsQuantity items depending on order, but after selecting requiredQuantity of items.
   * That means not all selected products will be discuounted & discounts are visualized after
   * meeting the required quantity.
   *
   * @returns true in case 2) from above.
   */ const isOnlyPartOfItemsDiscounted = (0, _react.useMemo)(()=>offerType === _basketUpdated.BasketOfferTypes.CHOOSE_WITH_DISCOUNT && requiredQuantity > benefitsQuantity, [
        benefitsQuantity,
        offerType,
        requiredQuantity
    ]);
    /**
   * @returns Array of discounted item codes where 1 item code can occur
   * more times and length of the array equals to benefitsQuantity.
   * This is being further used to calculate discounts for particular
   * benefit items.
   */ const discountedItems = (0, _react.useMemo)(()=>selectedBenefits.reduce((acc, param)=>{
            let { itemCode, selectedQuantity } = param;
            // if the benefit code is selected
            // add its code into the result as many times as equal to quantity
            for(let i = 0; i < selectedQuantity; i++){
                acc.push(itemCode);
            }
            return acc;
        }, [])// then cut the result by filtering so that the result contains
        // only as many items as benefitsQuantity count or less
        // first added items remain in array - order is important
        .filter((x, key)=>key < benefitsQuantity), [
        benefitsQuantity,
        selectedBenefits
    ]);
    /**
   * When isOnlyPartOfItemsDiscounted, the discounts are applied on selected benefits
   * only when isRequirementMet, otherwise the original price should be shown.
   * For non-selected items, the original unit price should be shown when isOnlyPartOfItemsDiscounted,
   * discount unit price should be shown otherwise.
   * Evaluation of discounts depends on order of benefit items in case of isOnlyPartOfItemsDiscounted.
   *
   * @returns Array of computed prices for 1st section items with optional discounts.
   */ const items1stSectionPrices = (0, _react.useMemo)(()=>{
        // Items will be gradually cut from this when applying discounted prices.
        const discountedArray = [
            ...discountedItems
        ];
        // Extracted here because of sonar cloud screaming about complexity.
        const getDiscountedPrices = (param)=>{
            let { itemCode, price: rawPrice, purchasePrice: rawPurchasePrice, selectedQuantity } = param;
            // via helper indexes array apply discounts to selected benefit items
            const indexes = Array.from({
                length: selectedQuantity
            });
            // compute the price for each selected quantity item
            const price = indexes.reduce((acc)=>{
                const discountedIndex = discountedArray.indexOf(itemCode);
                if (discountedIndex < 0) {
                    return acc + rawPurchasePrice;
                }
                // return the (discounted) price when discountedArray contains given item code
                // while also removing the discounted item from the temporary discounted array
                discountedArray.splice(discountedIndex, 1);
                return acc + rawPrice;
            }, 0);
            const purchasePrice = selectedQuantity * rawPurchasePrice;
            return {
                price,
                purchasePrice
            };
        };
        return items1stSection.map((param)=>{
            let { isBenefit, itemCode, price: rawPrice, purchasePrice: rawPurchasePrice, selectedQuantity } = param;
            let price;
            let purchasePrice;
            if (selectedQuantity === 0) {
                // show unit prices & don't show discount when isOnlyPartOfItemsDiscounted
                price = isBenefit && isOnlyPartOfItemsDiscounted ? rawPurchasePrice : rawPrice;
                purchasePrice = rawPurchasePrice;
            } else if (isBenefit && isOnlyPartOfItemsDiscounted && isRequirementMet) {
                // apply the discounts selectively to given amount of first selected items
                const result = getDiscountedPrices({
                    itemCode,
                    price: rawPrice,
                    purchasePrice: rawPurchasePrice,
                    selectedQuantity
                });
                price = result.price;
                purchasePrice = result.purchasePrice;
            } else {
                // don't show discounts when we have the isOnlyPartOfItemsDiscounted && !isRequirementMet case
                price = selectedQuantity * (isOnlyPartOfItemsDiscounted ? rawPurchasePrice : rawPrice);
                purchasePrice = selectedQuantity * rawPurchasePrice;
            }
            (0, _logger.logDebug)("prices for item ".concat(itemCode, " in offer ").concat(offerId, ":"), '\n\tprice', price, '\n\tpurchasePrice', purchasePrice);
            return {
                itemCode,
                selectedQuantity,
                price,
                purchasePrice
            };
        });
    }, [
        discountedItems,
        isOnlyPartOfItemsDiscounted,
        isRequirementMet,
        items1stSection,
        offerId
    ]);
    /**
   * Second section selectable items are so far shown only for choose & choose type of basket offer.
   * No special logic for discounts here like in 1st section. If there is a discount,
   * simply show it from start.
   *
   * @returns Array of computed prices for 2nd section items.
   */ const items2ndSectionPrices = (0, _react.useMemo)(()=>items2ndSection.map((param)=>{
            let { itemCode, price: rawPrice, purchasePrice: rawPurchasePrice, selectedQuantity } = param;
            const price = Math.max(selectedQuantity, 1) * rawPrice;
            const purchasePrice = Math.max(selectedQuantity, 1) * rawPurchasePrice;
            return {
                itemCode,
                selectedQuantity,
                price,
                purchasePrice
            };
        }), [
        items2ndSection
    ]);
    const totals1stSection = (0, _react.useMemo)(()=>sumTotals(items1stSectionPrices.filter((param)=>{
            let { selectedQuantity } = param;
            return selectedQuantity > 0;
        })), [
        items1stSectionPrices
    ]);
    const totals2ndSection = (0, _react.useMemo)(()=>offerType === _basketUpdated.BasketOfferTypes.CHOOSE_AND_GET ? getTotals(benefits) : getTotals(items2ndSection), [
        benefits,
        items2ndSection,
        offerType
    ]);
    const totalPrices = (0, _react.useMemo)(()=>sumTotals([
            totals1stSection,
            totals2ndSection
        ]), [
        totals1stSection,
        totals2ndSection
    ]);
    return {
        items1stSectionPrices,
        items2ndSectionPrices,
        totalPrices
    };
};
exports.useConceptOfferHintPrices = useConceptOfferHintPrices;
